import { useEffect, useState, useContext } from "react";

//Components
import VehicleDetail from "./VehicleDetail/VehicleDetail";
import {
  CircularProgress,
  AppBar,
  Tabs,
  Tab,
  Typography,
  Box,
} from "@material-ui/core";

import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";

//Styling
import { makeStyles } from "@material-ui/core/styles";

//Utils
import PropTypes from "prop-types";
import { vehicleAlertColor } from "../utils/utils";
import { createEmptyVis } from "../utils/EmptyObjects";
import { UserContext } from "../Contexts/UserContext";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { useMutation } from "@apollo/client";
import { ADD_OR_UPDATE_VEHICLE_IN_INCIDENT } from "../Mutations/IncidentMutations/UpdateVehicleInIncident";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box style={{ padding: "0px" }} p={1}>
          <Typography component={"div"}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `scrollable-auto-tab-${index}`,
    "aria-controls": `scrollable-auto-tabpanel-${index}`,
  };
}

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
}));

//This will hold all the tabs
const VehiclesDetail = ({
  showVehicleSearch,
  setShowVehicleSearch,
  vehiclesInfoLoading,
  vehiclesInfoData,
  vehiclesInfoError,
  viss,
  piss,
  incidentId,
  incidentUuid,
  incidentIndex,
  showVehiclesDetail,
  incidentOriId,
  userRoles,
  setPopper,
  _vehicleDetailIsMounted,
  ...props
}) => {
  const classes = useStyles();
  const [vehicleTab, setVehicleTab] = useState(0);
  const { onlineState, nightMode, responseErrorHandler, user } =
    useContext(UserContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const { UPDATE_INCIDENT, UPDATE_VIS, UPDATE_VEHICLE, ADD_VIS } =
    INCIDENT_ACTIONS;

  const handleChange = (event, newValue) => {
    setVehicleTab(newValue);
  };
  const [addVisToIncident] = useMutation(ADD_OR_UPDATE_VEHICLE_IN_INCIDENT, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const vis = data.addOrUpdateVehicleInIncident.vehicleIncidentSpecific;
        const visUuid =
          data.addOrUpdateVehicleInIncident.vehicleIncidentSpecificUuid;
        const vehicleUuid = data.addOrUpdateVehicleInIncident.vehicleUuid;

        //update the visId to be the non-uuid id
        incidentDispatch({
          type: UPDATE_VIS,
          payload: {
            incidentId: incidentId,
            incidentUuid: incidentUuid,
            visUuid: visUuid,
            property: "id",
            value: vis.id,
          },
        });
        //update the vehicleId to be the non-uuid id
        incidentDispatch({
          type: UPDATE_VEHICLE,
          payload: {
            incidentIndex: incidentIndex,
            vehicleUuid: vehicleUuid,
            property: "id",
            value: vis.vehicle.id,
          },
        });
      }
    },
  });

  const handleAddVisToIncident = () => {
    const localVis = createEmptyVis();
    incidentDispatch({
      type: ADD_VIS,
      payload: { incidentIndex: incidentIndex, vis: localVis },
    });
    addVisToIncident({
      variables: {
        input: {
          originatorId: user?.id,
          incidentId: incidentId ? incidentId : incidentUuid,
          vehicleId: localVis.vehicle.id,
          vehicleIncidentSpecificId: localVis.id,
        },
      },
      context: { serializationKey: "incidentQueue" },
    });
    setVehicleTab(viss?.length || 0);
  };

  useEffect(() => {
    if (vehicleTab >= viss?.length) {
      let newTab = viss?.length - 1 || 0;
      if (newTab < 0 || newTab === undefined) newTab = 0;
      setVehicleTab(newTab);
    }
  }, [viss, vehicleTab]);

  useEffect(() => {
    _vehicleDetailIsMounted.current = true;
    return () => {
      // ComponentWillUnmount in Class Component
      _vehicleDetailIsMounted.current = false;
    };
  }, [_vehicleDetailIsMounted]);

  useEffect(() => {
    if (viss.length === 0) {
      if (!onlineState && import.meta.env.VITE_FORCE_ONLINE !== "true") {
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incidentId,
            uuid: incidentUuid,
            property: "vehicleIncidentSpecifics",
            value: [createEmptyVis()],
          },
        });
        // console.log('Adding an offline vis in VehiclesDetail')
      } else {
        // console.log('Adding an online vis in VehiclesDetail')
        handleAddVisToIncident();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId, incidentUuid, onlineState, viss.length]);

  if (viss) {
    return (
      <div className={classes.root}>
        <AppBar position="static" color="default">
          <Tabs
            value={vehicleTab}
            onChange={handleChange}
            indicatorColor="primary"
            // textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable auto tabs example"
          >
            {viss.map((vis, index) => {
              let model = vis?.vehicle?.vehicleModel?.name
                ? vis.vehicle.vehicleModel.name
                : "";
              let make = vis?.vehicle?.vehicleMake?.name
                ? vis.vehicle.vehicleMake.name
                : "";
              let plate = vis?.vehicle?.vehiclePlate?.name
                ? vis.vehicle.vehiclePlate.name
                : "";
              let label =
                make === "" && model === "" && plate === ""
                  ? "New Vehicle"
                  : `${make} ${model}: ${plate}`;
              return (
                <Tab
                  style={{
                    color: nightMode
                      ? "white"
                      : vehicleAlertColor(vis?.vehicle),
                  }}
                  key={vis.id}
                  label={label}
                  {...a11yProps(index)}
                />
              );
            })}
          </Tabs>
        </AppBar>
        {viss.map((vis, index) => {
          return (
            <TabPanel key={vis.id} value={vehicleTab} index={index}>
              <VehicleDetail
                setPopper={setPopper}
                vis={vis}
                visIndex={index}
                incidentId={incidentId}
                incidentUuid={incidentUuid}
                incidentIndex={incidentIndex}
                piss={piss}
                vissLength={viss.length}
                vehicleTab={vehicleTab}
                setVehicleTab={setVehicleTab}
                incidentOriId={incidentOriId}
                userRoles={userRoles}
                showVehicleSearch={showVehicleSearch}
                setShowVehicleSearch={setShowVehicleSearch}
                {...props}
              />
            </TabPanel>
          );
        })}
      </div>
    );
  } else {
    return (
      <div>
        <CircularProgress data-cy="loading-circle" />
      </div>
    );
  }
};

export default VehiclesDetail;
